import axios from 'axios';

import { API_BASE_URL as baseUrl } from 'src/utils/api-url';

import { getAuth } from '../providers/AppAuthProvider';

const base = 'xbit-relay/provider-audit';

async function authHeaders() {
  const { accessToken } = (await getAuth()) || {};
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
  };
}

/**
 * The BE sends int64 fields (`total`, `*UnixMs`) as strings and money as decimal strings.
 * Types below mirror the wire shape exactly — convert at the point of use, never here.
 */
export type ProviderAuditFlow = 'deposit' | 'withdraw';

export type ProviderAuditMismatchItem = {
  id: string;
  userId: string;
  provider: string;
  triggerFlow: ProviderAuditFlow | string;
  triggerRef: string;
  ourDeposit: string;
  ourWithdraw: string;
  providerDeposit: string;
  providerWithdraw: string;
  residualDeposit: string;
  residualWithdraw: string;
  delta: string;
  createdAtUnixMs: string;
};

export type ProviderAuditMismatchFilters = {
  page?: number;
  pageSize?: number;
  userId?: string;
  provider?: string;
  timeFrom?: number;
  timeTo?: number;
};

export type ListProviderAuditMismatchesResponse = {
  items: ProviderAuditMismatchItem[];
  total: string;
  page: number;
  pageSize: number;
};

// `timeFrom: 0` is a meaningful epoch boundary — only drop undefined/null/''.
const toParams = (filters: Record<string, unknown>): Record<string, string> => {
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params[key] = String(value);
  });
  return params;
};

export async function listProviderAuditMismatches(
  filters: ProviderAuditMismatchFilters = {}
) {
  const headers = await authHeaders();
  return axios.get<ListProviderAuditMismatchesResponse>(`${baseUrl}/${base}/mismatches`, {
    headers,
    params: toParams(filters),
  });
}

export type ProviderAuditLockState = {
  userId: string;
  provider: string;
  locked: boolean;
  lockedAtUnixMs: string;
  reason: string;
};

export type ProviderAuditLockFilters = {
  userId?: string;
  provider?: string;
};

export type GetProviderAuditLocksResponse = {
  states: ProviderAuditLockState[];
};

/** Not paginated — omitting both filters lists every lock row, so always pass `userId`. */
export async function getProviderAuditLocks(filters: ProviderAuditLockFilters = {}) {
  const headers = await authHeaders();
  return axios.get<GetProviderAuditLocksResponse>(`${baseUrl}/${base}/locks`, {
    headers,
    params: toParams(filters),
  });
}

/** Business failures come back as HTTP 200 with `success: false` — always branch on it. */
export type ProviderAuditActionResponse = {
  success: boolean;
  message: string;
};

export type CreateProviderAuditCorrectionBody = {
  userId: string;
  provider: string;
  flow: ProviderAuditFlow | string;
  acceptedAmount: string;
  reason: string;
  refMismatchId?: string;
  expiresAtUnixMs?: number;
};

// `adminId` is never sent: the BE reads the actor from the JWT and ignores a body value.
export async function createProviderAuditCorrection(body: CreateProviderAuditCorrectionBody) {
  const headers = await authHeaders();
  return axios.post<ProviderAuditActionResponse>(`${baseUrl}/${base}/corrections`, body, {
    headers,
  });
}

export type UnlockProviderAuditBody = {
  userId: string;
  provider: string;
  reason: string;
};

export async function unlockProviderAudit(body: UnlockProviderAuditBody) {
  const headers = await authHeaders();
  return axios.post<ProviderAuditActionResponse>(`${baseUrl}/${base}/unlock`, body, {
    headers,
  });
}
